import React from 'react';
import ReactDOM from 'react-dom';
import { WindowConfig } from './windowTypes';
import { useWindowManager } from './WindowManager';
import WindowHeader from './WindowHeader';
import WindowBody from './WindowBody';
import WindowFooter from './WindowFooter';
import { ReactComponent as IconLoading } from '../../assets/icon-loading.svg';
import './Window.scss';

interface WindowProps {
  config: WindowConfig;
}

interface WindowComponent extends React.FC<WindowProps> {
  Body: typeof WindowBody;
  Footer: typeof WindowFooter;
  Header: typeof WindowHeader;
}

const WindowBase: React.FC<WindowProps> = ({ config }) => {
  const windowManager = useWindowManager();

  const maxZIndex = windowManager.windows.length > 0
    ? Math.max(...windowManager.windows.map(w => w.zIndex))
    : 0;
  const isTopWindow = config.zIndex === maxZIndex;

  const handleClose = () => {
    windowManager.closeWindow(config.id);
  };

  const handleWindowClick = () => {
    windowManager.focusWindow(config.id);
  };

  const Component = config.component;

  // No modo automático nenhuma medida vai no inline: o dimensionamento inteiro fica
  // no CSS, que é onde piso, teto e rolagem do corpo conseguem conviver.
  const automatica = config.size?.auto === true;

  const windowStyle: React.CSSProperties = automatica ? {} : {
    width: config.size?.width,
    ...(config.size?.height !== undefined && { height: config.size.height })
  };

  const content = (
    <div
      className={`window-backdrop ${isTopWindow ? 'window-backdrop-top' : ''}`}
      style={{ zIndex: config.zIndex }}
    >
      {config.loading ? (
        <div className="window-loading-only">
          <IconLoading />
        </div>
      ) : (
        <div className={`window${automatica ? ' window-auto' : ''}`} style={windowStyle} onClick={handleWindowClick}>
          <WindowHeader title={config.title} onClose={handleClose} />
          <Component {...(config.props || {})} data={config.data} />
        </div>
      )}
    </div>
  );

  const container = config.containerElement || document.body;

  return ReactDOM.createPortal(content, container);
};

const Window = WindowBase as WindowComponent;

Window.Body = WindowBody;
Window.Footer = WindowFooter;
Window.Header = WindowHeader;

export default Window;
